/// <amd-module name="Repos/Messages/ReposSuggestionsService" />
import { GitRepository, GitSuggestion } from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import * as Resources from "Repos/Messages/Resources";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Ago } from "VSSUI/Ago";
import { Icon } from "VSSUI/Icon";
import * as React from "react";
import { IRepositoryMessage } from "./Contracts";
import { getGitClient } from "./RestClient/Git";

interface IPullRequestSuggestionProperties {
    pushDate: Date;
    sourceBranch: string;
    sourceRepository: GitRepository;
    targetBranch: string;
    targetRepositoryId: string;
}

export interface IReposSuggestionsService extends IVssService {
    getSuggestions(): IReadonlyObservableArray<GitSuggestion>;
    dismissSuggestion(suggestion: GitSuggestion): void;
    getSuggestionMessage(suggestion: GitSuggestion): IRepositoryMessage | undefined;
}

export const PullRequestSuggestionMessageType = "PullRequestSuggestion";

const localStorageDismissedPullRequestSuggestionKey = "Repos.Suggestions.PullRequest.DismissedPushDate";

class ReposSuggestionsService extends VssService implements IReposSuggestionsService {
    private suggestions: ObservableArray<GitSuggestion>;

    public getSuggestions(): IReadonlyObservableArray<GitSuggestion> {
        if (!this.suggestions) {
            this.suggestions = new ObservableArray();
            if (userHasClaim(this.pageContext, UserClaims.Member)) {
                const gitRestClient = getGitClient(this.pageContext);
                const featureFlagEnabled = isFeatureFlagEnabled(this.pageContext, "WebAccess.VersionControl.PullRequests.UpdateTargetBranches2", false);

                let suggestionsPromise: Promise<GitSuggestion[]> | undefined;
                if (featureFlagEnabled) {
                    const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
                    const routeId = navigationService.getCurrentRoute().routeId;

                    if (routeId === "ms.vss-code-web.branches-route") {
                        // If we're on the Branches page, we prefer compare over default branch as PR target.
                        suggestionsPromise = gitRestClient.getSuggestions(getGitRepository(this.pageContext)!.id, undefined, true);
                    }
                }

                if (!suggestionsPromise) {
                    suggestionsPromise = gitRestClient.getSuggestions(getGitRepository(this.pageContext)!.id);
                }

                suggestionsPromise.then(suggestions => (this.suggestions.value = suggestions));
            }
        }
        return this.suggestions;
    }

    public dismissSuggestion(suggestion: GitSuggestion): void {
        this.suggestions.removeAll(i => i === suggestion);
        if (suggestion.type === "pullRequest") {
            const props = suggestion.properties as IPullRequestSuggestionProperties;
            trySetLocalStorageValue(localStorageDismissedPullRequestSuggestionKey, props.pushDate.getTime().toString());
        }
    }

    public getSuggestionMessage(suggestion: GitSuggestion): IRepositoryMessage | undefined {
        if (suggestion.type !== "pullRequest") {
            return undefined;
        }

        const props = suggestion.properties as IPullRequestSuggestionProperties;

        // Make sure the user didn't dismiss this message
        const lastDismissed = tryGetLocalStorageValue(localStorageDismissedPullRequestSuggestionKey);
        if (lastDismissed === props.pushDate.getTime().toString()) {
            return undefined;
        }

        const project = getTFSPageData(this.pageContext)!.project!;
        const currentRepository = getGitRepository(this.pageContext)!;

        const srcBranch = refToBranchName(props.sourceBranch);
        const tgtBranch = refToBranchName(props.targetBranch);

        const routingService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const branchUrl = routingService.getFilesUrl(
            props.sourceRepository.name,
            props.sourceRepository.project ? props.sourceRepository.project.id : project.id,
            undefined,
            branchToVersionDescriptor(srcBranch)
        );
        const pullRequestUrl = routingService.getPullRequestCreateUrl(
            currentRepository.name,
            project.id,
            srcBranch,
            tgtBranch,
            props.sourceRepository.id,
            props.targetRepositoryId
        );

        return {
            buttons: [
                {
                    href: pullRequestUrl,
                    text: Resources.CreateAPullRequest,
                    onClick: event => {
                        onClickFPS(this.pageContext, pullRequestUrl, true, event);
                    }
                }
            ],
            onDismiss: () => {
                this.dismissSuggestion(suggestion);
            },
            renderMessage: () => (
                <FormatComponent elementType="span" format={Resources.PullRequestSuggestionFormat}>
                    <span>
                        {props.sourceRepository.id !== currentRepository.id && (
                            <>
                                <Icon iconName="GitFork" className="margin-right-4" />
                                <span className="margin-right-4">{props.sourceRepository.name}</span>
                            </>
                        )}
                        <Icon iconName="OpenSource" className="margin-right-4" />
                        <FPSLink className="monospaced-text" href={branchUrl} removeUnderline={true}>
                            {srcBranch}
                        </FPSLink>
                    </span>
                    <Ago date={props.pushDate} />
                </FormatComponent>
            ),
            type: PullRequestSuggestionMessageType
        };
    }
}

Services.add("IReposSuggestionsService", { serviceFactory: ReposSuggestionsService });
